/**
 * Paid company holidays — the admin-owned calendar, and the pay it produces.
 *
 * A holiday is ONE company-wide fact, not a per-employee booking (contrast
 * `pto.ts`). Everyone carrying a HOLIDAY earning on their NetSuite record is paid
 * from the same row, so the calendar stays small and cannot disagree with itself.
 *
 * Everything here is PURE and ISO date-STRING based (`YYYY-MM-DD`, compared
 * lexicographically) so the identical rules run in the browser and on the API,
 * with no `Date` arithmetic and therefore no timezone drift.
 */
// clampToPayrollPeriod is no longer needed here: a single-date holiday cannot straddle
// a payroll period. It stays exported from payroll-period.ts because PTO still uses
// date RANGES and may want it.
import { EARNING_MAP } from './earning-map.generated.js';
import { listWorkDays } from './pto.js';
/**
 * Hours of holiday pay each eligible employee receives per holiday DAY.
 *
 * A full J&J work day (Michael, 2026-08-19: "10 hours of holiday pay"), matching
 * {@link import('./pto.js').MAX_PTO_HOURS_PER_DAY}. Pay is this many hours at the
 * employee's own rate for their holiday earning, so NetSuite computes
 * rate x hours exactly as it does for worked time.
 */
export const HOLIDAY_HOURS_PER_DAY = 10;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Validate a holiday. Returns EVERY broken rule at once, so the form can show
 * everything to fix in one pass.
 */
export function validateCompanyHoliday(input, context) {
    const issues = [];
    const name = input.name.trim();
    if (name === '') {
        issues.push({ field: 'name', message: 'Give the holiday a name.' });
    }
    const dateOk = ISO_DATE.test(input.date);
    if (!dateOk) {
        issues.push({ field: 'date', message: 'Pick a date.' });
    }
    if (dateOk) {
        // With a single date there is no range to be backwards and no overlap to compute:
        // a clash is simply another holiday on the SAME DATE. The old version needed a
        // start<=end check and an interval intersection to say the same thing.
        const clash = context.existing
            .filter((row) => row.id !== context.editingId)
            .find((row) => row.date === input.date);
        if (clash !== undefined) {
            const same = clash.name.trim().toLowerCase() === name.toLowerCase();
            issues.push({
                field: 'entry',
                message: same
                    ? `“${clash.name}” is already on the calendar for ${clash.date}.`
                    : `“${clash.name}” is already on ${clash.date} — two holidays cannot share a ` +
                        `date, or that day would be paid twice. Pick another date, or edit that ` +
                        `holiday instead.`,
            });
        }
    }
    return issues;
}
/**
 * The paid days a holiday covers — one date, or none.
 *
 * WORK DAYS ONLY (Mon–Fri). A holiday landing on a Saturday or Sunday pays nothing
 * and drops out of the calculation entirely.
 *
 * ⚠️ NOTE THIS IS *NOT* THE PTO RULE ANY MORE. PTO now counts weekends (Michael,
 * 2026-09-02) because J&J runs at the weekend; holidays deliberately still do not.
 * The practice supports it: Independence Day 2026 is recorded as Friday 3 July, not
 * Saturday the 4th, so weekend holidays are already shifted to a weekday by hand and
 * a holiday should never land on one. If one ever does, it pays nothing — which is
 * loud rather than subtle, and better than silently paying a day nobody agreed to.
 */
export function holidayPaidDays(holiday) {
    return listWorkDays(holiday.date, holiday.date);
}
/**
 * NetSuite payroll-item ids of every HOLIDAY earning, from EARNING_MAP.
 *
 * A FUNCTION, not a top-level constant, so the map is not pinned into every bundle
 * that imports the shared barrel — the same reason `visaEarningItemIds` is a function.
 * Two places need this list (the Holiday report's eligibility query and the poster),
 * and a second hand-maintained copy would drift the moment an earning is added.
 */
export function holidayEarningItemIds() {
    return EARNING_MAP.filter((m) => m.kind === 'holiday').map((m) => m.nsPayrollItemId);
}
/**
 * NetSuite departments a given holiday earning serves.
 *
 * Used ONLY as a tie-breaker by {@link resolveHolidayDepartment} — see the warning
 * there about why this must not be treated as a constraint.
 */
export function holidayEarningDepartments(nsPayrollItemId) {
    const found = EARNING_MAP.find((m) => m.kind === 'holiday' && m.nsPayrollItemId === nsPayrollItemId);
    return found === undefined ? [] : [...found.nsDepartmentIds];
}
/**
 * Which department an employee's holiday pay bills to.
 *
 * THE EMPLOYEE'S OWN DEPARTMENT DECIDES, not their earning. The earning sets the RATE
 * and the payroll item; the department is cost attribution, and they are independent
 * fields on a TimeBill. A single department is therefore accepted outright, whatever
 * the earning's mapping says.
 *
 * ⚠️ THIS IS THE MISTAKE WORTH NOT REPEATING. The first version intersected the
 * employee's departments with the earning's `nsDepartmentIds` from EARNING_MAP. Against
 * real data that rejected 12 of 45 eligible employees — including all NINE drivers,
 * who sit in department 11 (Transportation) while `HOLIDAY - HOURLY - DRIVING` is
 * mapped to 20 (Trailer shop). Their department is not ambiguous at all. EARNING_MAP's
 * department list exists to pick an earning FROM a department (the kiosk direction);
 * using it backwards as a constraint invents a conflict that is not there.
 *
 * The earning is consulted ONLY to break a genuine tie, which is the one case where
 * something has to choose: 20 employees belong to more than one department, and for
 * them the earning's departments narrow the field. If that still leaves more than one,
 * it REFUSES rather than picking — the same stance as `resolveEarning`, because an
 * arbitrary choice here silently books a day's pay to the wrong department and nothing
 * would ever say so.
 *
 * @param candidates every department the employee belongs to.
 * @param earningDepartmentNsIds NetSuite departments the employee's holiday earning
 *   serves, used only as a tie-breaker.
 */
export function resolveHolidayDepartment(candidates, earningDepartmentNsIds) {
    if (candidates.length === 0) {
        return {
            status: 'none',
            reason: 'employee belongs to no department — holiday pay has nowhere to bill to',
        };
    }
    const mapped = candidates.filter((c) => c.nsDepartmentId !== null && c.nsDepartmentId !== '');
    if (mapped.length === 0) {
        return {
            status: 'unmapped',
            reason: "employee's department is not mapped to a NetSuite department",
        };
    }
    // One department: done. No tie to break, so the earning is irrelevant.
    if (mapped.length === 1) {
        const only = mapped[0];
        return { status: 'resolved', departmentId: only.departmentId, nsDepartmentId: only.nsDepartmentId };
    }
    const narrowed = mapped.filter((c) => earningDepartmentNsIds.includes(c.nsDepartmentId));
    if (narrowed.length === 1) {
        const only = narrowed[0];
        return { status: 'resolved', departmentId: only.departmentId, nsDepartmentId: only.nsDepartmentId };
    }
    // Still undecided. Name the candidates so an admin can fix the membership rather
    // than being told only that it failed.
    const listed = (narrowed.length > 0 ? narrowed : mapped)
        .map((c) => c.nsDepartmentId)
        .sort()
        .join(', ');
    return {
        status: 'ambiguous',
        reason: `employee belongs to ${String(mapped.length)} departments and the holiday earning ` +
            `does not narrow it to one (candidates: ${listed}) — set their department so ` +
            `holiday pay bills to the right one`,
    };
}
/** Round to cents, so floating-point noise never reaches a money figure. */
function round2(value) {
    return Math.round(value * 100) / 100;
}
/**
 * Work out what a holiday pays.
 *
 * ELIGIBILITY IS THE NETSUITE RECORD (Michael, 2026-08-19): everyone holding a
 * HOLIDAY earning is paid, whatever their pay type or department — so this takes
 * the already-filtered list rather than deciding eligibility itself.
 *
 * Each eligible employee receives {@link HOLIDAY_HOURS_PER_DAY} hours per paid
 * day at their OWN rate for that earning. An employee with no rate on the line is
 * FLAGGED, never treated as zero: silently paying nothing is the failure mode the
 * earning resolver already refuses to make, and it would be invisible in a total.
 */
export function computeHolidayPay(holiday, eligible) {
    const paidDays = holidayPaidDays(holiday);
    const hoursPerEmployee = paidDays.length * HOLIDAY_HOURS_PER_DAY;
    const lines = eligible.map((earning) => {
        const hasRate = earning.rate !== null && Number.isFinite(earning.rate) && earning.rate > 0;
        return {
            employeeId: earning.employeeId,
            employeeName: earning.employeeName,
            nsPayrollItemId: earning.nsPayrollItemId,
            earningName: earning.earningName,
            rate: earning.rate,
            hours: hoursPerEmployee,
            amount: hasRate ? round2(earning.rate * hoursPerEmployee) : null,
            flag: hasRate
                ? null
                : `No rate on ${earning.earningName ?? `earning ${earning.nsPayrollItemId}`} in NetSuite — ` +
                    `add a rate to this employee's earning line before running payroll.`,
        };
    });
    const payable = lines.filter((line) => line.amount !== null);
    return {
        paidDays,
        hoursPerEmployee,
        lines,
        payableCount: payable.length,
        totalAmount: round2(payable.reduce((sum, line) => sum + (line.amount ?? 0), 0)),
        flaggedCount: lines.length - payable.length,
    };
}
/**
 * Holidays whose range overlaps a payroll period — the lookup holiday pay will
 * use once paycheck injection exists ("only when the date range for the holiday
 * falls within the payroll period being worked").
 *
 * A single date is either inside the period or it is not — the straddling case the
 * range version had to reason about cannot arise any more.
 */
export function holidaysInPeriod(holidays, periodStart, periodEnd) {
    return holidays.filter((h) => h.date >= periodStart && h.date <= periodEnd);
}
/**
 * What a payroll period owes in holiday pay — Michael's rule that holiday pay
 * applies "only when the date range for the holiday falls within the payroll
 * period that is being worked".
 *
 * Clamping is gone with the range: a single date is in the period or it is not, so
 * there is no straddling holiday to split and no way to pay one twice across a
 * boundary. That whole class of error stopped being possible rather than being
 * guarded against.
 */
export function computeHolidayPeriodPay(period, holidays, eligible) {
    const lines = [];
    for (const holiday of holidaysInPeriod(holidays, period.startDate, period.endDate)) {
        const pay = computeHolidayPay(holiday, eligible);
        // A holiday landing on a weekend pays nothing; keep it out rather than listing a
        // zero-hour entry that reads like an error.
        if (pay.hoursPerEmployee === 0) {
            continue;
        }
        lines.push({ holiday, pay });
    }
    return {
        period,
        holidays: lines,
        totalAmount: round2(lines.reduce((sum, l) => sum + l.pay.totalAmount, 0)),
        flaggedCount: lines.reduce((sum, l) => sum + l.pay.flaggedCount, 0),
    };
}
//# sourceMappingURL=holiday.js.map